/**
 * OpenAI TTS provider — Cloud TTS via OpenAI's audio.speech API.
 *
 * Voice quality is dramatically better than browser SpeechSynthesis. Six
 * voices map to the four Coach personalities. Pricing as of 2026:
 *   - tts-1:    $15 / 1M chars (good, fast)
 *   - tts-1-hd: $30 / 1M chars (better quality, slower)
 * A typical Coach paragraph is ~300-500 chars, so ~$0.005-$0.015/paragraph.
 *
 * Security: API key lives in settings.json + is read inside the main
 * process. Renderer never sees it. Same pattern as the Anthropic key.
 *
 * Audio playback: receives raw MP3 bytes via IPC, wraps in a Blob, plays
 * through an HTMLAudioElement. We hold one global audio element so stop()
 * can interrupt cleanly when the user kicks off a new speak() call.
 */

// Default voice mapping — each personality gets a voice that fits its
// vibe. User can override per-personality in Settings.
//   alloy  — neutral / professional
//   echo   — male, low-energy
//   fable  — warm British, calm
//   onyx   — deep male, authoritative (good for Drill)
//   nova   — female, friendly conversational (good for Mentor)
//   shimmer— female, warm (good for Friend)
export const OPENAI_VOICES = ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'];

export const OPENAI_DEFAULT_VOICE_MAP = Object.freeze({
  mentor:  'nova',
  analyst: 'alloy',
  drill:   'onyx',
  friend:  'shimmer',
});

export const OPENAI_MODELS = ['tts-1', 'tts-1-hd'];

// One global audio element — there's only one "currently speaking" stream
// at a time. Re-used across calls so we can stop() / start() cleanly.
let _audio = null;
let _objectUrl = null;

const cleanupAudio = () => {
  if (_audio) {
    try { _audio.pause(); } catch { /* */ }
    _audio.src = '';
    _audio = null;
  }
  if (_objectUrl) {
    try { URL.revokeObjectURL(_objectUrl); } catch { /* */ }
    _objectUrl = null;
  }
};

export const isAvailable = () => {
  return typeof window !== 'undefined' &&
         typeof window.electronAPI?.coachTtsCall === 'function' &&
         typeof window.Audio !== 'undefined';
};

/**
 * Fetch + play OpenAI TTS audio for the given text.
 * @param {string} text
 * @param {Object} options
 * @param {string} [options.voice='mentor']  Personality id
 * @param {Object} [options.voiceMap]        Optional override map { mentor: 'nova', ... }
 * @param {string} [options.model='tts-1']
 * @param {Function} [options.onStart]
 * @param {Function} [options.onEnd]
 * @returns {Promise<{ok:true} | {ok:false, error:string}>}
 */
export const speak = async (text, options = {}) => {
  if (!isAvailable()) {
    return { ok: false, error: 'OpenAI TTS unavailable (Electron IPC bridge missing)' };
  }
  if (typeof text !== 'string' || text.trim().length === 0) {
    return { ok: false, error: 'Empty text' };
  }
  const personality = options.voice || 'mentor';
  const voiceMap = options.voiceMap || OPENAI_DEFAULT_VOICE_MAP;
  const openaiVoice = voiceMap[personality] || OPENAI_DEFAULT_VOICE_MAP[personality] || 'nova';
  const model = options.model || 'tts-1';

  // Stop anything currently playing — single-stream contract.
  cleanupAudio();

  try {
    const res = await window.electronAPI.coachTtsCall({
      provider: 'openai',
      text,
      voice: openaiVoice,
      model,
    });
    if (!res?.ok) {
      return { ok: false, error: res?.error || 'TTS call failed' };
    }
    // res.audio is a Uint8Array (or Buffer-shaped) of MP3 bytes
    const bytes = res.audio instanceof Uint8Array ? res.audio : new Uint8Array(res.audio);
    const blob = new Blob([bytes], { type: res.contentType || 'audio/mpeg' });
    _objectUrl = URL.createObjectURL(blob);
    _audio = new window.Audio(_objectUrl);

    return await new Promise((resolve) => {
      _audio.onplay = () => {
        if (typeof options.onStart === 'function') options.onStart();
      };
      _audio.onended = () => {
        if (typeof options.onEnd === 'function') options.onEnd();
        cleanupAudio();
        resolve({ ok: true });
      };
      _audio.onerror = () => {
        if (typeof options.onEnd === 'function') options.onEnd();
        cleanupAudio();
        resolve({ ok: false, error: 'Audio playback failed' });
      };
      // play() returns a promise; rejected play (e.g., autoplay blocked) lands here
      const p = _audio.play();
      if (p && typeof p.catch === 'function') {
        p.catch((e) => {
          if (typeof options.onEnd === 'function') options.onEnd();
          cleanupAudio();
          resolve({ ok: false, error: e?.message || 'play() rejected' });
        });
      }
    });
  } catch (e) {
    cleanupAudio();
    return { ok: false, error: e?.message || String(e) };
  }
};

export const stop = () => {
  cleanupAudio();
};
